import React from 'react';
import PropTypes from 'prop-types';
import {
  FormattedMessage, defineMessages, useIntl,
} from 'react-intl';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import {
  useParams,
} from 'react-router-dom';
import { LegacyInputTextComponent } from '../../../components/prime-formik';
import FormikTextCopiable from '../../../components/formik-components/FormikTextCopiable';
import { validationMessages } from '../../../utils/FormUtils';


const i18n = defineMessages({
  headerMessage: { id: 'remove_header.respondent', defaultMessage: 'Remove Respondent' },
  reasonForRemoval: { id: 'lbl.remove_reason', defaultMessage: 'Reason for removal: ' },
  requestedByRespondent: { id: 'lbl.del_reason.respondent_request', defaultMessage: 'Requested by Respondent' },
  dataNoRelevant: { id: 'lbl.del_reason.data_no_relevant', defaultMessage: 'Data is no longer relevant to us' },
  removeConfirmWarning: { id: 'lbl.del_confirm.warning', defaultMessage: 'This action cannot be undone.' },
  confirmationCode: { id: 'lbl.confirmation_code', defaultMessage: 'Confirmation code' },
  confirmationCodeRetype: { id: 'lbl.confirmation_code_retype', defaultMessage: 'Please type the confirmation code' },
});

const DialogFooter = ({ cancelRemove }) => (
  <>
    <FormattedMessage id="actions.remove" defaultMessage="Remove">
      {text => (
        <Button label={text} className="p-button-danger" type="submit" />
      )}
    </FormattedMessage>
      &nbsp;
    <FormattedMessage id="actions.cancel" defaultMessage="Cancel">
      {text => (
        <Button label={text} className="p-button-primary" type="button" onClick={cancelRemove} />
      )}
    </FormattedMessage>
  </>
);

DialogFooter.propTypes = {
  cancelRemove: PropTypes.func.isRequired,
};

const RespondentRemoveConfirmForm = ({
  confirmRemove,
  cancelRemove,
  isVisible,
}) => {
  const { formatMessage } = useIntl();
  const { respondentId } = useParams();

  const initialValues = {
    confirmation_code: respondentId,
    re_confirmation_code: '',
  };

  const validationSchema = Yup.object().shape({
    reason: Yup.string().required(formatMessage(validationMessages.required)),
    confirmation_code: Yup.string().required(formatMessage(validationMessages.required)),
    re_confirmation_code: Yup.string()
      .oneOf([Yup.ref('confirmation_code'), null], validationMessages.sameAs)
      .required(formatMessage(validationMessages.required)),
  });

  return (
    <Formik
      onSubmit={confirmRemove}
      initialValues={initialValues}
      validationSchema={validationSchema}
      render={({ values, handleSubmit, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <Dialog
            header={formatMessage(i18n.headerMessage)}
            footer={<DialogFooter cancelRemove={cancelRemove} />}
            visible={isVisible}
            onHide={cancelRemove}
            modal
          >
            <div className="panel panel-no-border">
              <div className="panel-body">
                <div>
                  <div className="form-group">
                    <div>
                      {formatMessage(i18n.reasonForRemoval)}
                    </div>
                    <div className="form-group">
                      <Field
                        id="reason_request"
                        name="reason"
                        checked={values.reason === 'respondent_request'}
                        onChange={(e) => { setFieldValue('reason', e.value); }}
                        component={RadioButton}
                        value="respondent_request"
                      />
                      <label htmlFor="reason_request">
                        {formatMessage(i18n.requestedByRespondent)}
                      </label>
                    </div>

                    <div className="form-group">
                      <Field
                        name="reason"
                        id="reason_data_no_relevant"
                        component={RadioButton}
                        checked={values.reason === 'data_out_of_using'}
                        onChange={(e) => { setFieldValue('reason', e.value); }}
                        value="data_out_of_using"
                      />
                      <label htmlFor="reason_request">
                        {formatMessage(i18n.dataNoRelevant)}
                      </label>
                    </div>
                  </div>

                  <div className="form-group" style={{ color: 'red' }}>
                    <p>
                      <strong>
                        {formatMessage(i18n.removeConfirmWarning)}
                      </strong>
                    </p>
                  </div>

                  <div className="form-group group-normalized">
                    <div>
                      <label>
                        {formatMessage(i18n.confirmationCode)}
                      </label>
                    </div>
                    <Field name="confirmation_code" keyValue={respondentId} component={FormikTextCopiable} readOnly />

                    <div>
                      <label>
                        {formatMessage(i18n.confirmationCodeRetype)}
                      </label>
                    </div>
                    <Field name="re_confirmation_code" component={LegacyInputTextComponent} />
                  </div>
                </div>
              </div>
            </div>
          </Dialog>
        </form>
      )}
    />
  );
};

RespondentRemoveConfirmForm.propTypes = {
  cancelRemove: PropTypes.func.isRequired,
  confirmRemove: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
};

export default RespondentRemoveConfirmForm;
